import { Injectable, EventEmitter } from '@angular/core';
import {
  HttpClient,
  HttpEvent,
  HttpEventType,
  HttpParams,
  HttpRequest,
  HttpResponse,
  HttpErrorResponse,
  HttpHeaders,
} from '@angular/common/http';
import { firstValueFrom, throwError } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import * as S3 from 'aws-sdk/clients/s3';

import * as JSZip from 'jszip';
import { FileClass } from '../aws-s3-upload/file.class';
import { S3UploadService } from '../s3-upload.service';

// interfaces
import {
  IParamStartUpload,
  IS3Data,
} from '../aws-s3-upload/multipart.interface';

@Injectable({
  providedIn: 'root',
})
export class AwsS3UploadServiceDrive {
  uploadProgress$ = new EventEmitter<any>();
  finishedProgress$ = new EventEmitter<any>();

  url: string = 'https://api-dam.mango-soft.com/v3/prod';
  // url: string = 'http://localhost:3000';

  constructor(
    private httpClient: HttpClient,
    private s3UploadService: S3UploadService
  ) {}

  /**
   * Upload file by S3 AWS.
   *
   * @param file
   * @param tokenEmit
   * @param dataS3
   * @param payload
   */
  async uploadFile(
    file: File,
    tokenEmit: string,
    dataS3: IS3Data,
    payload: any
  ) {
    const contentType = file.type;
    const bucket = new S3({
      accessKeyId: dataS3.accessKeyId,
      secretAccessKey: dataS3.secretAccessKey,
      region: 'us-east-1',
    });

    const params = {
      Bucket: dataS3.bucket,
      Key: `${dataS3.folder ? dataS3.folder + '/' : ''}${file.name}`,
      Body: file,
      ACL: 'public-read',
      ContentType: contentType,
    };

    // for upload progress
    bucket
      .upload(params)
      .on('httpUploadProgress', (evt) => {
        // console.log(evt.loaded + ' of ' + evt.total + ' Bytes');
        const progress = Math.round((evt.loaded * 100) / evt.total);
        this.uploadProgress$.emit({
          progress: progress,
          token: tokenEmit,
          payload: payload,
        });

        if (progress >= 100) {
          this.finishedProgress$.emit({
            data: payload,
          });
        }
      })
      .send((err: any) => {
        if (err) {
          return false;
        }
        return true;
      });

    return await bucket.upload(params).promise();
  }

  // -------------------------------
  // --- Presigned URL -------------
  // -------------------------------

  /**
   * Initiate a multipart upload.
   *
   * @param params
   */
  private startUpload(params: IParamStartUpload): Promise<any> {
    const httpParams = new HttpParams()
      .set('fileName', encodeURIComponent(params.fileName))
      .set('fileType', encodeURIComponent(params.fileType))
      .set('bucket', encodeURIComponent(params.bucket));

    return this.httpClient
      .get(`${this.url}/start-upload`, { params: httpParams })
      .toPromise();
  }

  /**
   * Upload MultiPart.
   *
   * @param file
   * @param tokenEmit
   * @param dataS3
   * @param payload
   */
  async uploadMultipartFile(
    file: any,
    tokenEmit: string,
    dataS3: IS3Data,
    payload: any
  ) {
    const uploadStartResponse = await this.startUpload({
      fileName: file.name,
      fileType: file.type,
      bucket: dataS3.bucket,
    });

    try {
      const FILE_CHUNK_SIZE = 10000000; // 10MB
      const fileSize = file.size;
      const NUM_CHUNKS = Math.floor(fileSize / FILE_CHUNK_SIZE) + 1;
      let start, end, blob;

      let uploadPartsArray: { ETag: string; PartNumber: any }[] = [];
      let countParts = 0;

      let orderData: { presignedUrl: string; index: number }[] = [];

      for (let index = 1; index < NUM_CHUNKS + 1; index++) {
        start = (index - 1) * FILE_CHUNK_SIZE;
        end = index * FILE_CHUNK_SIZE;
        blob = index < NUM_CHUNKS ? file.slice(start, end) : file.slice(start);

        const httpParams = new HttpParams()
          .set('fileName', encodeURIComponent(file.name))
          .set('fileType', encodeURIComponent(file.type))
          .set('bucket', encodeURIComponent(dataS3.bucket))
          .set('partNumber', index.toString())
          .set('uploadId', uploadStartResponse.data.uploadId);

        // (1) Generate presigned URL for each part

        const uploadUrlPresigned = (await this.httpClient
          .get(`${this.url}/get-upload-url`, { params: httpParams })
          .toPromise()) as Object;

        // (2) Puts each file part into the storage server

        orderData.push({
          presignedUrl: uploadUrlPresigned.toString(),
          index: index,
        });

        const req = new HttpRequest(
          'PUT',
          uploadUrlPresigned.toString(),
          blob,
          {
            reportProgress: true,
          }
        );

        this.httpClient.request(req).subscribe((event: HttpEvent<any>) => {
          switch (event.type) {
            case HttpEventType.UploadProgress:
              const percentDone = Math.round(
                (100 * event.loaded) / FILE_CHUNK_SIZE
              );
              this.uploadProgress$.emit({
                progress: file.size < FILE_CHUNK_SIZE ? 100 : percentDone,
                token: tokenEmit,
                payload: payload,
              });
              break;
            case HttpEventType.Response:
              console.log('😺 Done!');
          }

          // (3) Calls the CompleteMultipartUpload endpoint in the backend server

          if (event instanceof HttpResponse) {
            const currentPresigned = orderData.find(
              (item) => item.presignedUrl === event.url
            ) as {
              presignedUrl: string;
              index: number;
            };

            countParts++;
            const newEvent = event.headers.get('ETag') as string;
            uploadPartsArray.push({
              ETag: newEvent.replace(/[|&;$%@'<>()+,]/g, ''),
              PartNumber: currentPresigned.index,
            });

            if (uploadPartsArray.length === NUM_CHUNKS) {
              this.httpClient
                .post(`${this.url}/complete-upload`, {
                  fileName: encodeURIComponent(file.name),
                  parts: uploadPartsArray.sort((a, b) => {
                    return a.PartNumber - b.PartNumber;
                  }),
                  uploadId: uploadStartResponse.data.uploadId,
                  bucket: dataS3.bucket,
                })
                .toPromise()
                .then((res) => {
                  this.finishedProgress$.emit({
                    data: res,
                  });
                });
            }
          }
        });
      }
    } catch (e) {
      console.log('error: ', e);
    }
  }

  /**
   * For cases of thousands of files with small sizes, do not form a very large zip in order not to delay
   * the compression, so generate several smaller zip.
   *
   * @param files
   * @param name
   */
  async processZipList(files: File[], name: string) {
    let returnZipFiles = [];
    const totalSizes = files.map((file) => file.size).reduce((a, b) => a + b);
    let parts = Math.round(totalSizes / 20971520);

    const splitListFiles = this.splitUpArray(files, parts);

    for (let i = 0; i < splitListFiles.length; i++) {
      const files = splitListFiles[i] as any[];
      const zip = await this.buildZip(files, `${name}_${i + 1}.mng`);
      returnZipFiles.push({
        zip: zip,
        files: files,
      });
    }

    return returnZipFiles;
  }

  /**
   * Build files zip.
   *
   * @param files
   * @param name
   */
  async buildZip(files: File[], name: string) {
    const zip = new JSZip();

    for (const file of files) {
      zip.file(file.name, file);
    }

    const zipFile = await zip.generateAsync({ type: 'blob' });
    // FileSaver.saveAs(zipFile, `${name}.zip`);
    return this.blobToFile(zipFile, `${name}.zip`);
  }

  /**
   * Convert blob to file.
   *
   * @param theBlob
   * @param fileName
   */
  blobToFile(theBlob: Blob, fileName: string): File {
    const b: any = theBlob;
    // A Blob() is almost a File() - it's just missing the two properties below which we will add
    b.lastModifiedDate = new Date();
    b.name = fileName;

    // Cast to a File() type
    return <File>theBlob;
  }

  /**
   * Splitting a JS array into N arrays.
   *
   * @param arr
   * @param n
   */
  splitUpArray(arr: string | any[], n: number) {
    const rest = arr.length % n;
    let restUsed = rest;
    const partLength = Math.floor(arr.length / n);
    const result = [];

    for (let i = 0; i < arr.length; i += partLength) {
      let end = partLength + i,
        add = false;

      if (rest !== 0 && restUsed) {
        end++;
        restUsed--;
        add = true;
      }

      result.push(arr.slice(i, end));

      if (add) {
        i++;
      }
    }

    return result;
  }

  /**
   * Get name of first folder.
   *
   * @param droppedFile
   */
  getNameFirstFolder(droppedFile: { relativePath: string }) {
    return droppedFile.relativePath.split('/')[0].replace(/[^A-Z0-9]+/gi, '_');
  }

  /**
   * Get real name of first folder.
   *
   * @param droppedFile
   */
  getRealNameFirstFolder(droppedFile: { relativePath: string }) {
    return droppedFile.relativePath.split('/')[0];
  }

  /**
   * Create object in the database before uploading.
   *
   * @param file
   * @param payloadToDB
   * @returns
   */
  async postCreateFileObject(file: File, payloadToDB?: any) {
    const headers: HttpHeaders = new HttpHeaders();
    const httpOptions = {
      headers: headers.set('Content-Type', 'application/jsonp'),
    };

    const fileObject = new FileClass(file);

    let body: any = {
      name: fileObject.name,
      type: fileObject.getNormalType(),
      size: fileObject.size,
      classified: 'classified',
      status: 'received',
      // owner: 'admin@zerofractal.com',
      classification: await fileObject.classify(),
    };

    if (payloadToDB) {
      if (payloadToDB.hasOwnProperty('tags')) {
        body.tags = payloadToDB.tags;
      }
    }

    // 'https://api-dam.mango-soft.com/v2/insert'
    const search$ = this.httpClient
      .post(`https://api-dam.mango-soft.com/v2/insert`, body, httpOptions)
      .pipe(
        catchError(this.handleError),
        map((response: any) => response)
      );

    return await firstValueFrom(search$);
  }

  /**
   * Execute step function to convert and update file uploaded in the database and DAM.
   *
   * @param createdObject
   * @param uploadedFile
   * @returns
   */
  async postStepFunction(createdObject: any, uploadedFile: any) {
    const domain = window.location.hostname;

    const headers: HttpHeaders = new HttpHeaders();
    const httpOptions = {
      headers: headers.set('Content-Type', 'application/jsonp'),
    };

    let type = uploadedFile.Key.split('.')[1].toUpperCase();
    type = type === 'JPEG' ? 'JPG' : type;

    if (this.s3UploadService.classify(type) === 'video') {
      type = 'video';
    }

    const body = {
      type: type,
      id: createdObject.file_id,
      file_name: uploadedFile.Key,
      folder: createdObject.folder,
      owner: `generic@${domain}`,
      userId: `generic@${domain}`,
      moduleName: 'QUERY-BROWSER',
      clientName: 'zerofractal',
    };

    // https://api-dam.mango-soft.com/v2/stepfunction
    const search$ = this.httpClient
      .post(`https://api-dam.mango-soft.com/v2/stepfunction`, body, httpOptions)
      .pipe(
        catchError(this.handleError),
        map((response: any) => response)
      );

    return await firstValueFrom(search$);
  }

  private handleError(error: HttpErrorResponse) {
    console.error(error);
    return throwError(() => error);
  }
}
